import Badge from '../../components/base/Badge';
import React from 'react';
import { Card, ProgressBar } from 'react-bootstrap';
import Button from '../../components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import useProjectProgress from './useProjectProgress';
import {
  faChevronRight,
  faBoxesStacked,
  faIndustry,
  faLayerGroup,
  faListCheck,
  faRulerHorizontal,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { PHASE_LABEL } from '../../helpers/standardPayload';

const fmtM = v => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '0';
  return n.toLocaleString('ro-RO', { maximumFractionDigits: 0 });
};

/**
 * One product, at a glance.
 *
 * What this used to show: the product label, a status badge, a planned length
 * with no unit, a progress bar — and **"Client: Gusteau's Restaurant"**, which
 * came with the template and was the same on all 28 cards. Nothing on a job
 * document says who the customer is; the allocation lives on the ORDER, and a
 * product can be split across several. What the job does carry is the split it
 * was launched with, so that is what the card says instead: how many metres were
 * promised to orders and how many are going to stock.
 *
 * Everything here comes from `adaptJobToProjectRow` — no second fetch, so the
 * grid stays as cheap to render as the list it sits beside.
 */
const CardViewItem = ({ project }) => {
  const { progress, bgClassName, variant } = useProjectProgress(project);
  const navigate = useNavigate();

  const dest = project.destination || {};
  const phases = Array.isArray(project.phases) ? project.phases : [];
  const machines = Array.isArray(project.machines) ? project.machines : [];
  const done = project.progressTokens?.min ?? 0;
  const total = project.progressTokens?.max ?? project.drums ?? 0;

  const open = () =>
    navigate(`/apps/project-management/project-details/${project.jobId}`);

  return (
    <Card className="h-100 hover-actions-trigger">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-start">
          <h4
            className="mb-1 line-clamp-2 lh-sm flex-1 me-3 cursor-pointer"
            onClick={open}
          >
            {project.productLabel}
          </h4>
          <div className="hover-actions top-0 end-0 mt-4 me-4">
            <Button
              variant="primary"
              className="btn-icon flex-shrink-0"
              onClick={open}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>

        {/* The lot number. It is what gets read out on the phone and written on
            the drum, so it belongs on the front of the card. */}
        <div className="fs-10 text-700 mb-2 font-monospace">
          {project.jobCode || project.jobIdShort}
        </div>

        <div className="d-flex gap-2 flex-wrap mb-3">
          <Badge variant="phoenix" bg={project.status.type} className="fs-10">
            {project.status.label}
          </Badge>
          {project._deleted && (
            <Badge variant="phoenix" bg="danger" className="fs-10">
              DELETED
            </Badge>
          )}
        </div>

        {/* ── where it is going ── */}
        <div className="d-flex align-items-center mb-2">
          <FontAwesomeIcon
            icon={faBoxesStacked}
            className="me-2 text-700 fs-9 fw-extra-bold"
          />
          <p className="fw-bold mb-0 text-truncate lh-1 fs-9">
            For:{' '}
            <span className="fw-semi-bold text-primary ms-1">{dest.label || '—'}</span>
            {dest.toOrders > 0 && dest.toStock > 0 && (
              <span className="fw-normal text-700 ms-1 fs-10">
                ({fmtM(dest.toOrders)} / {fmtM(dest.toStock)} m)
              </span>
            )}
          </p>
        </div>

        {/* ── length: made of planned, not planned alone ── */}
        <div className="d-flex align-items-center mb-2">
          <FontAwesomeIcon
            icon={faRulerHorizontal}
            className="me-2 text-700 fs-9 fw-extra-bold"
          />
          <p className="fw-bold mb-0 text-truncate lh-1 fs-9">
            Length:{' '}
            <span className="text-1100 ms-1">
              {fmtM(project.executedLength)} / {fmtM(project.plannedLength)} m
            </span>
          </p>
        </div>

        {machines.length > 0 && (
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon
              icon={faIndustry}
              className="me-2 text-700 fs-9 fw-extra-bold"
            />
            <p className="fw-bold mb-0 text-truncate lh-1 fs-9">
              Machines:{' '}
              <span className="fw-semi-bold text-1100 ms-1 text-uppercase">
                {machines.join(', ')}
              </span>
            </p>
          </div>
        )}

        {phases.length > 0 && (
          <div className="d-flex align-items-start mb-3">
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="me-2 mt-1 text-700 fs-9 fw-extra-bold"
            />
            <div className="d-flex gap-1 flex-wrap">
              {phases.map(p => (
                <span key={p} className="badge badge-phoenix badge-phoenix-info fs-10">
                  {PHASE_LABEL[p] || p}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between text-700 fw-semi-bold">
          <p className="mb-2 fs-9">Progress</p>
          <p className="mb-2 text-1100 fs-9">{progress}%</p>
        </div>
        <ProgressBar
          now={progress}
          className={classNames('flex-1', bgClassName)}
          variant={variant}
        />

        {/* Pushed to the bottom so cards of different heights still line their
            dates up with each other. */}
        <div className="mt-auto">
          <div className="d-flex justify-content-between mt-3 fs-10">
            <span className="fw-bold">
              Started:
              <span className="fw-semi-bold text-600 ms-1">{project.start}</span>
            </span>
          </div>
          <div className="d-flex justify-content-between fs-10">
            <span className="fw-bold">
              Due:
              <span className="fw-semi-bold text-600 ms-1">{project.deadline}</span>
            </span>
            {project.createdBy && (
              <span className="fw-bold">
                <FontAwesomeIcon icon={faUser} className="me-1 text-700" />
                <span className="fw-semi-bold text-600">{project.createdBy}</span>
              </span>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 fs-9">
            <span>
              <FontAwesomeIcon icon={faListCheck} className="me-1" />
              <p className="d-inline-block fw-bold mb-0">
                {project.task}
                <span className="fw-normal"> tasks</span>
              </p>
            </span>
            {total > 0 && (
              <span className="fw-bold">
                {done}/{total}
                <span className="fw-normal text-700"> drums</span>
              </span>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default CardViewItem;
